type Params<T extends string> = Record<T, string>

function buildDynamicPath<T extends string>(template: string, params: Params<T>): string {
  return Object.entries(params).reduce((acc, [key, value]) => acc.replace(`:${key}`, String(value)), template)
}

// Every route leaf MUST declare these three fields. `buildPath` is deliberately absent
// from the constraint: leaves implement it with a narrow `Params<...>` param plus `this`,
// and restating the wide signature here would reintroduce a variance error. It flows
// through structurally via the `const T` inference in `defineRoute` instead.
export type RouteLeafRequired = {
  path: string
  fullPath: string
  pageType: string
}

// Wrap every route leaf so `pageType` is a REQUIRED field — omitting it is a local
// compile error at that leaf. The `const` type parameter preserves the literal pageType
// (so `RoutePageType` can be derived from the table) and the verbatim `buildPath`
// signature + `this` type.
export function defineRoute<const T extends RouteLeafRequired>(route: T): T {
  return route
}

/** Matches the B2B staff console's every path, its home included. */
export const B2B_STAFF_ANY_PATH = '/work/staff/*'

/** The NoPressure B2B portal: `/work` and everything under it. Every other path is the original portal. */
export const B2B_PORTAL_PATH_PREFIX = '/work'

/**
 * Whether a path is in the B2B portal: `/work` itself or anything under `/work/`, compared case-insensitively as the router
 * matches. A saved link may carry `?…` and `#…`, so those are dropped first. `/workspaces/…`, `/workx` and the old
 * `/b/…` and `/b2b/…` are not.
 */
export function isB2bPortalPath(path: string): boolean {
  const pathname = (path.split(/[?#]/, 1)[0] ?? '').toLowerCase()
  return pathname === B2B_PORTAL_PATH_PREFIX || pathname.startsWith(`${B2B_PORTAL_PATH_PREFIX}/`)
}

export const ROUTES = {
  ROOT: defineRoute({ path: '/', fullPath: '/', pageType: 'app_root' }),

  // Public auth flow
  AUTH: {
    ROOT: defineRoute({ path: 'auth', fullPath: '/auth', pageType: 'auth_root' }),
    LOGIN: defineRoute({ path: 'login', fullPath: '/auth/login', pageType: 'login' }),
  },
  ACCEPT_TERMS_OF_SERVICE: defineRoute({
    path: 'accept-terms-of-service',
    fullPath: '/accept-terms-of-service',
    pageType: 'accept_terms_of_service',
  }),
  ONBOARDING: defineRoute({ path: 'onboarding', fullPath: '/onboarding', pageType: 'onboarding' }),
  TERMS_OF_SERVICE: defineRoute({
    path: 'terms-of-service',
    fullPath: '/terms-of-service',
    pageType: 'terms_of_service',
  }),
  // Public support page. Like TERMS_OF_SERVICE it renders for logged-out visitors:
  // unguarded in router.tsx AND 401-exempt in STATUS_REDIRECT_RULES (api/setup.ts),
  // so the boot-time getMe() 401 can't bounce a visitor to login.
  CUSTOMER_SUPPORT: defineRoute({
    path: 'support',
    fullPath: '/support',
    pageType: 'customer_support',
  }),
  ORGANIZATION_INVITE: defineRoute({
    path: 'invititation',
    fullPath: '/invititation',
    pageType: 'accept_invite',
    buildPath(params: Params<'organizationId'>) {
      return `${this.fullPath}?organizationId=${params.organizationId}`
    },
  }),
  // Public invite-link landing route. PUBLIC (renders for logged-out users) and
  // 401-EXEMPT (see STATUS_REDIRECT_RULES in api/setup.ts) so neither auto-saver
  // ever persists the token-bearing URL to localStorage (plan §5.1).
  INVITE: defineRoute({ path: 'invite', fullPath: '/invite', pageType: 'invite' }),
  // App pages
  WORKSPACE_LIBRARY: defineRoute({
    path: 'workspaces/:id/library',
    fullPath: '/workspaces/:id/library',
    pageType: 'scenario_library',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  CREATE_SCENARIO: defineRoute({
    path: 'workspaces/:id/create-scenario',
    fullPath: '/workspaces/:id/create-scenario',
    pageType: 'create_scenario',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  // The backend builds links to this route and to SCENARIO_PLAYBACK (backend/src/app/common/web_app_links.py:
  // invite email, MCP hand-off redirect, Grok Bot practice link); renaming either means changing it there too.
  ORGANIZATION_DETAIL: defineRoute({
    path: 'workspaces/:id',
    fullPath: '/workspaces/:id',
    pageType: 'workspace_detail',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  SCENARIO_PLAYBACK: defineRoute({
    path: 'workspaces/:id/scenario/:scenarioId',
    fullPath: '/workspaces/:id/scenario/:scenarioId',
    pageType: 'roleplay',
    keepScreenAwake: true, // hold a Screen Wake Lock while on this route (ScreenWakeLockManager)
    buildPath(params: Params<'id' | 'scenarioId'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  // The NoPressure B2B portal, all under B2B_PORTAL_PATH_PREFIX. Its pages never call the original workspace endpoints.
  // Never name a param `id` (AppLayout reads it as the workspace) or `sessionId` (the original flow's): a B2B session is
  // `b2bSessionId`, as in the backend, and analytics sends it as `b2b_session_id` (lib/analytics/pageContext.ts).
  B2B: {
    // Opens the first workspace's workflows, or says the person is in none.
    HOME: defineRoute({ path: 'work', fullPath: B2B_PORTAL_PATH_PREFIX, pageType: 'b2b_home' }),
    // The sidebar's "Workflows": the workspace's workflows the person may read.
    WORKSPACE_WORKFLOWS: defineRoute({
      path: 'work/workspaces/:workspaceId/workflows',
      fullPath: '/work/workspaces/:workspaceId/workflows',
      pageType: 'b2b_workspace_workflows',
      buildPath(params: Params<'workspaceId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // One workflow: what anyone on it reads, and what its managers run (versions, members, sessions, finishing).
    WORKFLOW: defineRoute({
      path: 'work/workflows/:workflowId',
      fullPath: '/work/workflows/:workflowId',
      pageType: 'b2b_workflow',
      buildPath(params: Params<'workflowId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // One's own session in a workflow, the one per person: its memory and activity. A learner's workflow page forwards
    // here once they started; a manager opens it from their own Sessions row.
    WORKFLOW_SESSION: defineRoute({
      path: 'work/workflows/:workflowId/session/:b2bSessionId',
      fullPath: '/work/workflows/:workflowId/session/:b2bSessionId',
      pageType: 'b2b_workflow_session',
      buildPath(params: Params<'workflowId' | 'b2bSessionId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // One page per simulation, "preparing" then the practice. The backend builds this link for learners' chats
    // (common/web_app_links.py b2b_simulation_page_url); renaming it means changing it there.
    SIMULATION: defineRoute({
      path: 'work/simulations/:simulationId',
      fullPath: '/work/simulations/:simulationId',
      pageType: 'b2b_simulation',
      keepScreenAwake: true, // hold a Screen Wake Lock while on this route (ScreenWakeLockManager)
      buildPath(params: Params<'simulationId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // Public like the original ones, and 401-exempt in STATUS_REDIRECT_RULES (api/setup.ts).
    TERMS_OF_SERVICE: defineRoute({
      path: 'work/terms-of-service',
      fullPath: '/work/terms-of-service',
      pageType: 'b2b_terms_of_service',
    }),
    CUSTOMER_SUPPORT: defineRoute({
      path: 'work/support',
      fullPath: '/work/support',
      pageType: 'b2b_customer_support',
    }),
    // A workflow's public invitation page, `/work/invite#<code>`; also 401-exempt. The backend builds this link
    // (common/web_app_links.py b2b_workflow_invite_page_url); renaming it means changing it there.
    INVITE: defineRoute({ path: 'work/invite', fullPath: '/work/invite', pageType: 'b2b_invite' }),
    // The original My account page with only its account details and deletion cards.
    ACCOUNT: defineRoute({ path: 'work/account', fullPath: '/work/account', pageType: 'b2b_account_settings' }),
    // For the workspace's admins: its members and their workspace roles.
    WORKSPACE_MEMBERS: defineRoute({
      path: 'work/workspaces/:workspaceId/members',
      fullPath: '/work/workspaces/:workspaceId/members',
      pageType: 'b2b_workspace_members',
      buildPath(params: Params<'workspaceId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // Where the workspace invitation email's Accept and Decline buttons lead: accept at once, or decline once asked.
    // The backend builds these links (common/web_app_links.py b2b_workspace_invitation_accept_page_url and
    // b2b_workspace_invitation_decline_page_url); renaming them means changing them there.
    WORKSPACE_INVITATION_ACCEPT: defineRoute({
      path: 'work/workspaces/:workspaceId/invitation/accept',
      fullPath: '/work/workspaces/:workspaceId/invitation/accept',
      pageType: 'b2b_workspace_invitation_accept',
      buildPath(params: Params<'workspaceId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    WORKSPACE_INVITATION_DECLINE: defineRoute({
      path: 'work/workspaces/:workspaceId/invitation/decline',
      fullPath: '/work/workspaces/:workspaceId/invitation/decline',
      pageType: 'b2b_workspace_invitation_decline',
      buildPath(params: Params<'workspaceId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // For the workspace's admins: its name and its authoring guide.
    WORKSPACE_SETUP: defineRoute({
      path: 'work/workspaces/:workspaceId/setup',
      fullPath: '/work/workspaces/:workspaceId/setup',
      pageType: 'b2b_workspace_setup',
      buildPath(params: Params<'workspaceId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // The staff console ("B2B Debug"), for staff only (`can_use_staff_console` on /me).
    STAFF: defineRoute({ path: 'work/staff', fullPath: '/work/staff', pageType: 'b2b_staff_workspaces' }),
    STAFF_WORKSPACE: defineRoute({
      path: 'work/staff/workspaces/:workspaceId',
      fullPath: '/work/staff/workspaces/:workspaceId',
      pageType: 'b2b_staff_workspace',
      buildPath(params: Params<'workspaceId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    STAFF_WORKFLOW: defineRoute({
      path: 'work/staff/workspaces/:workspaceId/workflows/:workflowId',
      fullPath: '/work/staff/workspaces/:workspaceId/workflows/:workflowId',
      pageType: 'b2b_staff_workflow',
      buildPath(params: Params<'workspaceId' | 'workflowId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    STAFF_SESSION: defineRoute({
      path: 'work/staff/workspaces/:workspaceId/workflows/:workflowId/sessions/:b2bSessionId',
      fullPath: '/work/staff/workspaces/:workspaceId/workflows/:workflowId/sessions/:b2bSessionId',
      pageType: 'b2b_staff_session',
      buildPath(params: Params<'workspaceId' | 'workflowId' | 'b2bSessionId'>) {
        return buildDynamicPath(this.fullPath, params)
      },
    }),
    // Any other path under it: the console's own "not found".
    STAFF_UNKNOWN: defineRoute({ path: 'work/staff/*', fullPath: B2B_STAFF_ANY_PATH, pageType: 'b2b_staff_unknown' }),
    // Any other path in the portal: the B2B "not found", never the original catch-all that resolves a workspace.
    NOT_FOUND: defineRoute({ path: 'work/*', fullPath: '/work/*', pageType: 'b2b_not_found' }),
  },
  SESSION_FEEDBACK: defineRoute({
    path: 'workspaces/:id/scenario/:scenarioId/sessions/:sessionId',
    fullPath: '/workspaces/:id/scenario/:scenarioId/sessions/:sessionId',
    pageType: 'scenario_feedback',
    buildPath(params: Params<'id' | 'scenarioId' | 'sessionId'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  SESSION_SIGNALS: defineRoute({
    path: 'workspaces/:id/scenario/:scenarioId/sessions/:sessionId/signals',
    fullPath: '/workspaces/:id/scenario/:scenarioId/sessions/:sessionId/signals',
    pageType: 'session_signals',
    buildPath(params: Params<'id' | 'scenarioId' | 'sessionId'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  SESSION_KEY_MOMENTS: defineRoute({
    path: 'workspaces/:id/scenario/:scenarioId/sessions/:sessionId/key-moments',
    fullPath: '/workspaces/:id/scenario/:scenarioId/sessions/:sessionId/key-moments',
    pageType: 'session_key_moments',
    buildPath(params: Params<'id' | 'scenarioId' | 'sessionId'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  SESSION_FEEDBACK_REGENERATE: defineRoute({
    path: 'workspaces/:id/scenario/:scenarioId/sessions/:sessionId/regenerate',
    fullPath: '/workspaces/:id/scenario/:scenarioId/sessions/:sessionId/regenerate',
    pageType: 'regenerate_feedback',
    buildPath(params: Params<'id' | 'scenarioId' | 'sessionId'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  EDIT_SCENARIO: defineRoute({
    path: 'workspaces/:id/edit-scenario/:scenarioId',
    fullPath: '/workspaces/:id/edit-scenario/:scenarioId',
    pageType: 'edit_scenario',
    buildPath(params: Params<'id' | 'scenarioId'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  MY_PATH: defineRoute({
    path: 'workspaces/:id/my-path',
    fullPath: '/workspaces/:id/my-path',
    pageType: 'my_path',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  MY_PATH_DEBUG: defineRoute({
    path: 'workspaces/:id/my-path/debug',
    fullPath: '/workspaces/:id/my-path/debug',
    pageType: 'my_path_debug',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  ANALYTICS: defineRoute({
    path: 'workspaces/:id/analytics',
    fullPath: '/workspaces/:id/analytics',
    pageType: 'analytics',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  WORKSPACE_MEMBERS: defineRoute({
    path: 'workspaces/:id/workspace-members',
    fullPath: '/workspaces/:id/workspace-members',
    pageType: 'workspace_members',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  // Admin-facing invite-link management. URL-only (no nav button — plan §5.8/D13);
  // protected guard like other /workspaces/:id pages; capability-gated by the API.
  INVITE_LINKS: defineRoute({
    path: 'workspaces/:id/invite-links',
    fullPath: '/workspaces/:id/invite-links',
    pageType: 'invite_links',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  WORKSPACE_SETTINGS: defineRoute({
    path: 'workspaces/:id/settings',
    fullPath: '/workspaces/:id/settings',
    pageType: 'workspace_settings',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  ACCOUNT_SETTINGS: defineRoute({
    path: 'workspaces/:id/account',
    fullPath: '/workspaces/:id/account',
    pageType: 'account_settings',
    buildPath(params: Params<'id'>) {
      return buildDynamicPath(this.fullPath, params)
    },
  }),
  CLONE_WORKSPACE: defineRoute({
    path: 'clone-workspace/:templateId',
    fullPath: '/clone-workspace/:templateId',
    pageType: 'clone_workspace',
  }),
  CLONE_WORKSPACE_SCENARIO: defineRoute({
    path: 'clone-workspace/:templateId/scenario/:scenarioId',
    fullPath: '/clone-workspace/:templateId/scenario/:scenarioId',
    pageType: 'clone_workspace_scenario',
  }),
  API_DOCS: defineRoute({ path: 'api-docs', fullPath: '/api-docs', pageType: 'api_docs' }),
}

/** The home of the portal a path is in: `/work` in the B2B portal, `/` in the original one. */
export function portalHomePath(path: string): string {
  return isB2bPortalPath(path) ? ROUTES.B2B.HOME.fullPath : ROUTES.ROOT.fullPath
}

// Recursively unions the `pageType` literal of every route leaf (including the nested
// AUTH and B2B groups). Deriving the union from the table — rather than hand-maintaining
// an enum — means the set of page types can never drift from the routes.
type ExtractPageTypes<T> = T extends { pageType: infer P }
  ? P
  : T extends object
    ? { [K in keyof T]: ExtractPageTypes<T[K]> }[keyof T]
    : never

export type RoutePageType = ExtractPageTypes<typeof ROUTES>

export const EXTERNAL_URLS = {
  PRIVACY_POLICY: 'https://www.jetbrains.com/legal/docs/privacy/privacy/',
} as const
